import { useTranslation } from 'react-i18next'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion'
import { cn } from '@/lib/utils'
import type { SpheraColumn, SpheraPreviewRow } from '@/types'

interface SpheraPreviewListProps {
	rows: SpheraPreviewRow[]
	columns: SpheraColumn[]
}

/** Uma linha compacta por registro; abrir mostra todos os campos do arquivo. */
export function SpheraPreviewList({ rows, columns }: SpheraPreviewListProps) {
	const { t } = useTranslation()

	return (
		<Accordion className="divide-y rounded-md border" type="multiple">
			{rows.map((row) => {
				const { values } = row
				const pending = row.issues.length > 0
				return (
					<AccordionItem
						key={values.uniqueId}
						value={values.uniqueId}
						className="border-b-0 px-3"
					>
						<AccordionTrigger className="gap-3 py-2 hover:no-underline">
							<span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left sm:flex-row sm:items-center sm:gap-4">
								<span className="flex shrink-0 items-center gap-2">
									<span
										className={cn(
											'size-2 shrink-0 rounded-full',
											pending ? 'bg-amber-400' : 'bg-green-500',
										)}
										title={
											pending
												? t('administration.spheraExport.preview.pending')
												: undefined
										}
									/>
									<span className="font-mono text-xs text-kaefer-dark-grey">
										{values.uniqueId}
									</span>
									<span className="text-xs text-muted-foreground">
										{values.dateOccurred} {values.timeOccurred}
									</span>
								</span>
								<span className="min-w-0 truncate text-xs text-muted-foreground">
									{[values.flag, values.location, values.specificService]
										.filter(Boolean)
										.join(' · ') || '—'}
								</span>
							</span>
						</AccordionTrigger>
						<AccordionContent>
							<dl className="grid gap-x-6 gap-y-2 pb-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
								{columns.map((column) => (
									<div key={column.key} className="min-w-0">
										<dt className="text-muted-foreground">{column.label}</dt>
										<dd className="break-words text-kaefer-dark-grey">
											{values[column.key] || '—'}
										</dd>
									</div>
								))}
							</dl>
						</AccordionContent>
					</AccordionItem>
				)
			})}
		</Accordion>
	)
}
